import { useMemo, useRef } from 'react';
import { Cog, PackageCheck, Wrench } from 'lucide-react';
import { motion } from 'framer-motion';
import { useI18n } from '../../context/I18nContext';
import AnimatedEyebrow from '../AnimatedEyebrow';
import ImageOnlySkeletonLoader from '../ImageOnlySkeletonLoader';

const offers = [
  {
    titleKey: 'aboutPage.offer.items.0.title',
    descriptionKey: 'aboutPage.offer.items.0.description',
    icon: Cog,
    image: '/services/1.webp',
  },
  {
    titleKey: 'aboutPage.offer.items.1.title',
    descriptionKey: 'aboutPage.offer.items.1.description',
    icon: PackageCheck,
    image: '/services/2.webp',
  },
  {
    titleKey: 'aboutPage.offer.items.2.title',
    descriptionKey: 'aboutPage.offer.items.2.description',
    icon: Wrench,
    image: '/services/3.webp',
  },
];

function splitTitleParts(text = '') {
  const words = String(text || '').trim().split(/\s+/).filter(Boolean);
  if (words.length <= 1) return { line1: String(text || '').trim(), line2: '' };
  return {
    line1: words.slice(0, -1).join(' '),
    line2: words[words.length - 1],
  };
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.14,
      delayChildren: 0.08,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.75, ease: [0.22, 1, 0.36, 1] },
  },
};

export default function AboutOfferSection() {
  const { t } = useI18n();
  const glowRef = useRef(null);
  const glowRafRef = useRef(null);
  const visibleOffers = useMemo(() => offers.filter((_, index) => index !== 2), []);

  const titleParts = useMemo(() => {
    const line1 = String(t('aboutPage.offer.titleLine1', '') || '').trim();
    const line2 = String(t('aboutPage.offer.titleLine2', '') || '').trim();
    if (line1 || line2) {
      return {
        line1: line1 || String(t('aboutPage.offer.title', '') || '').trim(),
        line2,
      };
    }

    const full = String(t('aboutPage.offer.title', '') || '').trim();
    const words = full.split(/\s+/).filter(Boolean);
    if (words.length <= 1) return { line1: full, line2: '' };
    return {
      line1: words.slice(0, -1).join(' '),
      line2: words[words.length - 1],
    };
  }, [t]);

  const handleSectionMouseMove = (event) => {
    if (glowRafRef.current) return;
    const clientX = event.clientX;
    const clientY = event.clientY;
    const rect = event.currentTarget.getBoundingClientRect();
    glowRafRef.current = requestAnimationFrame(() => {
      glowRafRef.current = null;
      const x = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
      const y = Math.max(0, Math.min(100, ((clientY - rect.top) / rect.height) * 100));
      if (glowRef.current) {
        glowRef.current.style.backgroundImage = `radial-gradient(circle at ${x}% ${y}%, rgba(255,93,16,0.10) 0%, transparent 42%)`;
      }
    });
  };

  return (
    <section
      className="relative overflow-hidden bg-[var(--dash-blacker)] py-40"
      onMouseMove={handleSectionMouseMove}
    >
      <div className="absolute inset-0 opacity-20 bg-[url('/bg/5.webp')] bg-no-repeat bg-[size:100%_100%]" />
      <div className="pointer-events-none absolute inset-0 z-[1]">
        <div
          ref={glowRef}
          className="absolute left-0 top-0 h-full w-full transition-[background-image] duration-150 ease-out"
          style={{ backgroundImage: 'radial-gradient(circle at 45% 35%, rgba(255,93,16,0.10) 0%, transparent 42%)' }}
        />
      </div>
      <div className="pointer-events-none absolute right-0 top-0 h-[460px] w-[460px] translate-x-1/4 -translate-y-1/3 rounded-full bg-[#ff5d10]/10 blur-[120px]" />
      <div className="pointer-events-none absolute bottom-0 left-0 h-[420px] w-[420px] -translate-x-1/4 translate-y-1/3 rounded-full bg-blue-500/10 blur-[120px]" />

      <div className="container relative z-10 mx-auto px-6 lg:px-10">
        <motion.div
          className="mb-10 max-w-3xl"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-120px' }}
        >
          <motion.div variants={itemVariants}>
            <AnimatedEyebrow className="mb-6">{t('aboutPage.offer.eyebrow')}</AnimatedEyebrow>
          </motion.div>
          <motion.h2 variants={itemVariants} className="text-4xl font-light uppercase leading-tight text-white sm:text-5xl">
            <span className="block">{titleParts.line1}</span>
            {titleParts.line2 ? (
              <span className="block font-black bg-gradient-to-r from-orange-400 via-[var(--dash-main)] to-orange-700 bg-clip-text text-transparent">
                {titleParts.line2}
              </span>
            ) : null}
          </motion.h2>
        </motion.div>

        <motion.div
          className="grid gap-6 md:grid-cols-2"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-120px' }}
        >
          {visibleOffers.map((offer) => {
            const offerTitle = t(offer.titleKey);
            const offerTitleParts = splitTitleParts(offerTitle);
            return (
              <motion.article
                key={offer.titleKey}
                variants={itemVariants}
                className="group relative overflow-hidden rounded-t-2xl border border-white/15 bg-black/25 backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:border-[var(--dash-other)]/60"
              >
                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1 w-0 bg-[var(--dash-other)] transition-all duration-500 group-hover:w-full" />
                <div className="relative z-10 overflow-hidden">
                  <ImageOnlySkeletonLoader
                    src={offer.image}
                    mode="native"
                    alt={t(offer.titleKey)}
                    containerClassName="h-72 w-full"
                    imgClassName="h-72 w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="relative z-10 p-6 py-10">
                  <div className="">
                    <h3 className="text-xl font-light uppercase !leading-[1.2] tracking-[0.03em] text-white">
                      {offerTitleParts.line1}{' '}
                      {offerTitleParts.line2 ? (
                        <span className="block font-black text-[var(--dash-main)]">{offerTitleParts.line2}</span>
                      ) : null}
                    </h3>
                    <p className="mt-3 text-sm leading-relaxed text-slate-400">{t(offer.descriptionKey)}</p>
                  </div>
                </div>
              </motion.article>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
